import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControlLabel, Alert, CircularProgress, Chip, Divider,
  TextField, Tooltip, List, ListItem, ListItemIcon, ListItemText,
} from '@mui/material';
import { Send, Email, WarningAmber, CheckCircle, Cancel, Mail } from '@mui/icons-material';
import { format, addMonths } from 'date-fns';

function getDefaultMonth() {
  // Default to next-but-one month (5 weeks ahead is typically the following month)
  return format(addMonths(new Date(), 2), 'yyyy-MM');
}

export default function BookingLetters() {
  const [month, setMonth] = useState(getDefaultMonth());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(false);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (dialogOpen) {
      loadSpecialties();
    }
  }, [dialogOpen]);

  const loadSpecialties = async () => {
    setLoadingSpecialties(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/specialties', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSpecialties(data);
      // Pre-select all that have an email address
      setSelected(data.filter(s => s.contact_email).map(s => s.id));
    } catch {
      setError('Failed to load specialties');
    } finally {
      setLoadingSpecialties(false);
    }
  };

  const toggleSpecialty = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const selectAll = () => setSelected(specialties.filter(s => s.contact_email).map(s => s.id));
  const selectNone = () => setSelected([]);

  const handleSend = async () => {
    if (!selected.length) return;
    setSending(true);
    setError('');
    setResults(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post('/api/reports/booking-letters',
        { month, specialtyIds: selected },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResults(data.results);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send booking letters');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    if (sending) return;
    setDialogOpen(false);
    setResults(null);
    setError('');
    setSelected([]);
  };

  const monthLabel = (() => {
    try {
      return format(new Date(`${month}-15`), 'MMMM yyyy');
    } catch {
      return month;
    }
  })();

  const specialtiesWithEmail = specialties.filter(s => s.contact_email);
  const specialtiesWithoutEmail = specialties.filter(s => !s.contact_email);
  const selectedCount = selected.length;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Monthly Booking Letters</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Generate and email monthly booking schedules to selected specialties. Each specialty
        receives an Excel spreadsheet listing all their confirmed room bookings for the chosen month.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Month"
          type="month"
          size="small"
          value={month}
          onChange={e => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 200 }}
        />
        <Button
          variant="contained"
          startIcon={<Mail />}
          onClick={() => setDialogOpen(true)}
          size="large"
        >
          Send Booking Letters…
        </Button>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Booking letter emails are sent to the email address configured against each specialty.
          To add or update email addresses, go to <strong>Admin &rarr; Specialties</strong>.
        </Typography>
      </Box>

      {/* ── Dialog ── */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Send Booking Letters — {monthLabel}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {results ? (
            // ── Results view ──
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Results:
              </Typography>
              <List dense>
                {results.map((r, i) => (
                  <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {r.status === 'sent'    && <CheckCircle color="success" fontSize="small" />}
                      {r.status === 'failed'  && <Cancel color="error" fontSize="small" />}
                      {r.status === 'skipped' && <WarningAmber color="warning" fontSize="small" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={r.specialty}
                      secondary={
                        r.status === 'sent'
                          ? `Sent to ${r.email.split(',').map(e => e.trim()).join(', ')} (${r.bookingCount} booking${r.bookingCount !== 1 ? 's' : ''})`
                          : r.status === 'failed'
                          ? `Failed: ${r.reason}`
                          : r.reason
                      }
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'bold' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : loadingSpecialties ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            // ── Selection view ──
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select the specialties to send letters to for <strong>{monthLabel}</strong>.
                Only specialties with a configured email address can be selected.
              </Typography>

              {specialtiesWithEmail.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Button size="small" onClick={selectAll}>Select all</Button>
                  <Button size="small" onClick={selectNone}>Select none</Button>
                </Box>
              )}

              {specialtiesWithEmail.length === 0 ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  No specialties have an email address configured. Go to Admin &rarr; Specialties to add them.
                </Alert>
              ) : (
                <List dense sx={{ mb: 1 }}>
                  {specialtiesWithEmail.map(s => (
                    <ListItem key={s.id} disablePadding>
                      <FormControlLabel
                        sx={{ width: '100%', mx: 0 }}
                        control={
                          <Checkbox
                            checked={selected.includes(s.id)}
                            onChange={() => toggleSpecialty(s.id)}
                            size="small"
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Box
                              sx={{
                                width: 12, height: 12, borderRadius: '2px',
                                backgroundColor: s.color, flexShrink: 0, mt: '3px',
                              }}
                            />
                            <Box>
                              <Typography variant="body2">{s.name}</Typography>
                              {s.contact_email.split(',').map(e => e.trim()).filter(Boolean).map((addr, i) => (
                                <Typography key={i} variant="caption" color="text.secondary" display="block">
                                  {addr}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}

              {specialtiesWithoutEmail.length > 0 && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    No email configured (cannot send):
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {specialtiesWithoutEmail.map(s => (
                      <Tooltip key={s.id} title="Add an email in Admin → Specialties">
                        <Chip
                          label={s.name}
                          size="small"
                          icon={<WarningAmber fontSize="small" />}
                          color="warning"
                          variant="outlined"
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {results ? (
            <Button onClick={handleClose} variant="contained">Close</Button>
          ) : (
            <>
              <Button onClick={handleClose} disabled={sending}>Cancel</Button>
              <Button
                onClick={handleSend}
                variant="contained"
                disabled={sending || selectedCount === 0}
                startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send />}
              >
                {sending
                  ? 'Sending…'
                  : `Send to ${selectedCount} specialty${selectedCount !== 1 ? 's' : ''}`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
